import { useState } from "react";
import {
  TransactionDueDateListModel,
  TransactionDueDateSearchParamsModel,
  ResponseBaseModel,
} from "../../models";
import { searchTransactionDueDate } from "../../serviceBroker/transactionDueDateServiceBroker";

// Interface for data state
interface TransactionDueDateDataState {
  data: TransactionDueDateListModel[];
  loading: boolean;
  totalCount: number;
}

const DEFAULT_PAGE_SIZE = 10;

export const useTransactionDueDate = (
  initialParams: TransactionDueDateSearchParamsModel = {}
) => {
  // Split into two separate state hooks
  const [searchParams, setSearchParams] =
    useState<TransactionDueDateSearchParamsModel>({
      ...initialParams,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });

  const [dataState, setDataState] = useState<TransactionDueDateDataState>({
    data: [],
    loading: false,
    totalCount: 0,
  });

  // Fetch data function
  const fetchData = async (params = searchParams) => {
    setDataState((current) => ({ ...current, loading: true }));

    try {
      const result = await searchTransactionDueDate(params);

      setDataState((current) => ({
        ...current,
        data: result?.Result || [],
        totalCount: result?.TotalRecoredCount || 0,
        loading: false,
      }));
      return result;
    } catch (error) {
      setDataState((current) => ({ ...current, loading: false }));
      return null;
    }
  };

  // Update search param
  const updateSearchParam = (key: string, value: any) => {
    setSearchParams((current) => ({
      ...current,
      [key]: value,
    }));
  };

  // Handle page change
  const onCurrentPageChange = (pageNumber: number) => {
    setSearchParams((current) => ({
      ...current,
      pageNumber,
    }));

    fetchData({
      ...searchParams,
      pageNumber,
    });
  };

  // Handle page size change
  const onPageSizeChange = (pageSize: number) => {
    setSearchParams((current) => ({
      ...current,
      pageSize,
      pageNumber: 1, // Reset to first page when changing page size
    }));

    fetchData({
      ...searchParams,
      pageSize,
      pageNumber: 1,
    });
  };

  // Reset search
  const resetSearch = () => {
    const resetParams = {
      pageNumber: 1,
      pageSize: searchParams.pageSize || DEFAULT_PAGE_SIZE,
    };

    setSearchParams(resetParams);

    return resetParams;
  };

  return {
    data: dataState.data,
    loading: dataState.loading,
    searchParams,
    totalCount: dataState.totalCount,

    // Actions
    fetchData,
    updateSearchParam,
    resetSearch,
    onCurrentPageChange,
    onPageSizeChange,
  };
};
